/**
 * Keep the user's API key out of PostHog session recordings (AI-10227).
 *
 * Recordings store page text as rendered, and several pages render the key:
 * Settings → API Keys, the Datamates setup commands, the dbt onboarding
 * command. Elements that show a key carry `NO_CAPTURE_CLASS`; every other text
 * node passes through `maskCredentialText`, so a key rendered anywhere else is
 * masked too. Recordings also copy attributes, which the text mask never sees,
 * so an element whose link carries a secret param is blocked whole
 * (`CREDENTIAL_LINK_SELECTOR`).
 */
import {
  ENCODED_PARAM_SEPARATORS,
  PARAM_JOINERS,
  PARAM_SEPARATORS,
  redactSecretParamsInText,
  SECRET_PARAM_NAMES,
} from "./sanitize-url";

// PostHog leaves elements with this class out of autocapture, and session
// recordings draw them as an empty placeholder (rrweb `blockClass`).
export const NO_CAPTURE_CLASS = "ph-no-capture";

// `%` written as the CSS escape `\25 `: browsers read a bare `%3D` literally,
// but jsdom's selector engine decodes it to `=`, so tests would check the wrong text.
const cssString = (text: string) => text.replace(/%/g, "\\25 ");

/**
 * Matches an element whose `href` carries a secret query or hash param, in any
 * form `redactSecretParamsInText` catches (rrweb `blockSelector`).
 */
export const CREDENTIAL_LINK_SELECTOR = [...PARAM_SEPARATORS, ...ENCODED_PARAM_SEPARATORS]
  .flatMap(separator =>
    SECRET_PARAM_NAMES.flatMap(name =>
      PARAM_JOINERS.map(joiner => `[href*="${cssString(separator + name + joiner)}" i]`)
    )
  )
  .join(", ");

// API keys are 32 hex characters (`secrets.token_hex(16)` in the backend). The
// key must stand alone: a longer run of letters or digits is something else.
const API_KEY_SHAPE_RE = /(^|[^0-9a-z])([0-9a-f]{32})(?![0-9a-z])/gi;

/**
 * Mask API-key-shaped strings and secret URL params in a recorded text node.
 * Wired as the session recorder's `maskTextFn`.
 */
export function maskCredentialText(text: string): string {
  return redactSecretParamsInText(text).replace(
    API_KEY_SHAPE_RE,
    (_match, before: string, key: string) => before + "*".repeat(key.length)
  );
}
